import { api, downloadFile } from '@config/api';
import type {
  Allocation,
  AllocationBody,
  ListAllocationsResponse,
  ValidationResponse,
} from '../types/Allocation';
import type { AllocationDefaults, AllocationDefaultsParams } from '../types/AllocationDefaults';
import type {
  PoolFilters,
  PoolResponse,
  UnallocatedFilters,
  UnallocatedResponse,
} from '../types/Checks';
import type {
  AllocationFilters,
  ClientOption,
  ColumnsExportOptions,
  ContractOption,
  ContractSearchParams,
  ExportOptions,
  PaginatedListResponse,
} from '../types/Filters';
import type { PercentageCheckFilters, PercentageCheckResponse } from '../types/PercentageCheck';
import type { ProfessionalOption } from '../types/Professional';
import type { ProjectOption } from '../types/Project';
import type { SalesProfileOption } from '../types/SalesProfile';
import type { SummaryFilters, SummaryResponse } from '../types/Summary';
import type {
  BatchAllocationTagsRequest,
  BatchAllocationTagsResult,
  TagOption,
} from '../types/Tag';
import type { TimelineFilters, TimelineResponse } from '../types/Timeline';

const LOOKUP_PER_PAGE = 100;

/** Listas vão como `1,2,3`, que o back aceita; o axios mandaria `ids[]=1&ids[]=2`. */
const joinIds = (ids: number[] | undefined) => ids?.join(',');

export const allocationService = {
  list: ({ professionalIds, tagIds, ...rest }: AllocationFilters, signal?: AbortSignal) =>
    api
      .get<ListAllocationsResponse>('/allocations', {
        params: { ...rest, professionalIds: joinIds(professionalIds), tagIds: joinIds(tagIds) },
        signal,
      })
      .then((r) => r.data),

  /** Com `page` (e `perPage`), só aquela página da tabela; sem, todas. */
  exportSpreadsheet: (
    { professionalIds, tagIds, ...rest }: Omit<AllocationFilters, 'page'> & { page?: number },
    { format, columns, language }: ColumnsExportOptions,
  ) =>
    downloadFile('/allocations/export', {
      ...rest,
      professionalIds: joinIds(professionalIds),
      tagIds: joinIds(tagIds),
      format,
      columns: columns.join(','),
      language,
    }),

  timeline: ({ professionalIds, tagIds, status, ...rest }: TimelineFilters, signal?: AbortSignal) =>
    api
      .get<TimelineResponse>('/allocations/timeline', {
        params: {
          ...rest,
          professionalIds: joinIds(professionalIds),
          tagIds: joinIds(tagIds),
          status: status?.join(','),
        },
        signal,
      })
      .then((r) => r.data),

  listPool: ({ professionalIds, projectIds, tagIds, ...rest }: PoolFilters, signal?: AbortSignal) =>
    api
      .get<PoolResponse>('/allocations/pool', {
        params: {
          ...rest,
          professionalIds: joinIds(professionalIds),
          projectIds: joinIds(projectIds),
          tagIds: joinIds(tagIds),
        },
        signal,
      })
      .then((r) => r.data),

  /** Com `page` (e `perPage`), só aquela página da consulta; sem, todas. */
  exportPool: (
    { professionalIds, projectIds, tagIds, ...rest }: Omit<PoolFilters, 'page'> & { page?: number },
    { format, columns, language }: ColumnsExportOptions,
  ) =>
    downloadFile('/allocations/pool/export', {
      ...rest,
      professionalIds: joinIds(professionalIds),
      projectIds: joinIds(projectIds),
      tagIds: joinIds(tagIds),
      format,
      columns: columns.join(','),
      language,
    }),

  listUnallocated: (
    { professionalIds, contractTypeIds, contractStatusIds, ...rest }: UnallocatedFilters,
    signal?: AbortSignal,
  ) =>
    api
      .get<UnallocatedResponse>('/allocations/unallocated', {
        params: {
          ...rest,
          professionalIds: joinIds(professionalIds),
          contractTypeIds: joinIds(contractTypeIds),
          contractStatusIds: joinIds(contractStatusIds),
        },
        signal,
      })
      .then((r) => r.data),

  /** Com `page` (e `perPage`), só aquela página da consulta; sem, todas. */
  exportUnallocated: (
    {
      professionalIds,
      contractTypeIds,
      contractStatusIds,
      ...rest
    }: Omit<UnallocatedFilters, 'page'> & { page?: number },
    { format, columns, language }: ColumnsExportOptions,
  ) =>
    downloadFile('/allocations/unallocated/export', {
      ...rest,
      professionalIds: joinIds(professionalIds),
      contractTypeIds: joinIds(contractTypeIds),
      contractStatusIds: joinIds(contractStatusIds),
      format,
      columns: columns.join(','),
      language,
    }),

  listSummary: (filters: SummaryFilters, signal?: AbortSignal) =>
    api
      .get<SummaryResponse>('/allocations/summary', { params: { ...filters }, signal })
      .then((r) => r.data),

  exportSummary: (filters: SummaryFilters, { format, language }: ExportOptions) =>
    downloadFile('/allocations/summary/export', {
      ...filters,
      format,
      language,
    }),

  listPercentageCheck: (filters: PercentageCheckFilters, signal?: AbortSignal) =>
    api
      .get<PercentageCheckResponse>('/allocations/percentage-check', {
        params: {
          ...filters,
          professionalIds: joinIds(filters.professionalIds),
          contractTypeIds: joinIds(filters.contractTypeIds),
          contractStatusIds: joinIds(filters.contractStatusIds),
          unitIds: joinIds(filters.unitIds),
        },
        signal,
      })
      .then((r) => r.data),

  exportPercentageCheck: (
    filters: Omit<PercentageCheckFilters, 'page'> & { page?: number },
    { format, columns, language }: ColumnsExportOptions,
  ) =>
    downloadFile('/allocations/percentage-check/export', {
      ...filters,
      professionalIds: joinIds(filters.professionalIds),
      contractTypeIds: joinIds(filters.contractTypeIds),
      contractStatusIds: joinIds(filters.contractStatusIds),
      unitIds: joinIds(filters.unitIds),
      format,
      columns: columns.join(','),
      language,
    }),

  create: (body: AllocationBody) => api.post<Allocation>('/allocations', body).then((r) => r.data),

  update: (id: number, body: Partial<AllocationBody>) =>
    api.patch<Allocation>(`/allocations/${id}`, body).then((r) => r.data),

  close: (id: number, endDate: string | null) =>
    api.post<Allocation>(`/allocations/${id}/close`, { endDate }).then((r) => r.data),

  batchUpdateTags: (body: BatchAllocationTagsRequest) =>
    api.post<BatchAllocationTagsResult>('/allocations/tags/batch', body).then((r) => r.data),

  /** Custo, moedas e rateio que o formulário preenche (mesmas regras do save no back). */
  getDefaults: (params: AllocationDefaultsParams, signal?: AbortSignal) =>
    api.get<AllocationDefaults>('/allocations/defaults', { params, signal }).then((r) => r.data),

  validate: (body: AllocationBody & { id?: number }, signal?: AbortSignal) =>
    api.post<ValidationResponse>('/allocations/validation', body, { signal }).then((r) => r.data),

  searchClients: (search: string, signal?: AbortSignal) =>
    api
      .get<PaginatedListResponse<ClientOption>>('/clients', {
        params: { search, perPage: LOOKUP_PER_PAGE },
        signal,
      })
      .then((r) => r.data),

  /** `allocatable` deixa de fora os contratos encerrados (combo do `AlocacaoForm.php`). */
  searchContracts: (
    search: string,
    { clientId, allocatable }: ContractSearchParams = {},
    signal?: AbortSignal,
  ) =>
    api
      .get<PaginatedListResponse<ContractOption>>('/contracts', {
        params: { search, clientId, allocatable, perPage: LOOKUP_PER_PAGE },
        signal,
      })
      .then((r) => r.data),

  searchProjects: (search: string, contractId?: number, signal?: AbortSignal) =>
    api
      .get<PaginatedListResponse<ProjectOption>>('/projects', {
        params: { search, contractId, perPage: LOOKUP_PER_PAGE },
        signal,
      })
      .then((r) => r.data),

  searchProfessionals: (search: string, multiAllocated?: boolean, signal?: AbortSignal) =>
    api
      .get<PaginatedListResponse<ProfessionalOption>>('/professionals', {
        params: { search, multiAllocated },
        signal,
      })
      .then((r) => r.data),

  /** Perfis de um contrato cabem numa página: o combobox filtra em memória. */
  listSalesProfiles: (contractId: number, signal?: AbortSignal) =>
    api
      .get<PaginatedListResponse<SalesProfileOption>>('/sales-profiles', {
        params: { contractId, perPage: LOOKUP_PER_PAGE },
        signal,
      })
      .then((r) => r.data),

  searchTags: (search: string, signal?: AbortSignal) =>
    api
      .get<PaginatedListResponse<TagOption>>('/allocation-tags', { params: { search }, signal })
      .then((r) => r.data),

  /** Opções do filtro da coluna Tags: todas as ativas, página a página (o QA já passa de 100). */
  listTagOptions: async (signal?: AbortSignal): Promise<TagOption[]> => {
    const tags: TagOption[] = [];
    for (let page = 1; ; page++) {
      const { data } = await api.get<PaginatedListResponse<TagOption>>('/allocation-tags', {
        params: { page, perPage: LOOKUP_PER_PAGE },
        signal,
      });
      tags.push(...data.items);
      if (data.items.length === 0 || tags.length >= data.total) return tags;
    }
  },
};
